import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, LogOut, Settings, GraduationCap, Loader2, Crown, Home, Users, MessageCircle, HelpCircle, Bot } from 'lucide-react';

interface NavbarProps {
  currentPage?: 'dashboard' | 'membre' | 'admin' | 'chat' | 'faq';
  onNavigate?: (page: 'dashboard' | 'membre' | 'admin' | 'chat' | 'faq') => void;
}

export const Navbar: React.FC<NavbarProps> = ({ currentPage = 'dashboard', onNavigate }) => {
  const { user, profile, signOut, updateProfile } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: profile?.name || '',
    role: profile?.role || 'student',
    subscription_type: profile?.subscription_type || 'visitor'
  });

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdating(true);
    await updateProfile(profileForm);
    setUpdating(false);
    setShowProfile(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Debug: Log profile info
  console.log('Navbar - Profile:', profile);
  console.log('Navbar - Is Teacher:', profile?.role === 'teacher');

  return (
    <>
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50 sprintbac-shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img 
                src="/images/IMG-20250728-WA0002.jpg" 
                alt="Sprintbac" 
                className="h-10 w-auto logo-pulse"
              />
              <span className="text-2xl font-bold sprintbac-hero-gradient bg-clip-text text-transparent">
                Sprintbac
              </span>
            </div>

            {/* Navigation */}
            <div className="flex items-center space-x-4">
              <Button
                variant={currentPage === 'dashboard' ? 'default' : 'ghost'}
                onClick={() => onNavigate?.('dashboard')}
                className={currentPage === 'dashboard' ? 'sprintbac-gradient' : ''}
              >
                <Home className="mr-2 h-4 w-4" />
                Tableau de bord
              </Button>
              
              <Button
                variant={currentPage === 'membre' ? 'default' : 'ghost'}
                onClick={() => onNavigate?.('membre')}
                className={currentPage === 'membre' ? 'sprintbac-gradient' : 'text-yellow-600 hover:text-yellow-700'}
              >
                <Crown className="mr-2 h-4 w-4" />
                Membre
              </Button>
              
              {/* Admin Button - Always show for debugging */}
              <Button
                variant={currentPage === 'admin' ? 'default' : 'ghost'}
                onClick={() => onNavigate?.('admin')}
                className={currentPage === 'admin' ? 'sprintbac-gradient' : 'text-blue-600 hover:text-blue-700'}
              >
                <Users className="mr-2 h-4 w-4" />
                Admin
                {profile?.role === 'teacher' && (
                  <span className="ml-1 text-xs bg-green-500 text-white px-1 rounded">✓</span>
                )}
              </Button>
              
              {/* Chat IA - Réservé aux membres et enseignants */}
              {(profile?.subscription_type === 'membre' || profile?.subscription_type === 'premium' || profile?.role === 'teacher') && (
                <Button
                  variant={currentPage === 'chat' ? 'default' : 'ghost'}
                  onClick={() => onNavigate?.('chat')}
                  className={currentPage === 'chat' ? 'sprintbac-gradient' : 'text-purple-600 hover:text-purple-700'}
                >
                  <Bot className="mr-2 h-4 w-4" />
                  Chat IA
                </Button>
              )}
              
              {/* FAQ Forum */}
              <Button
                variant={currentPage === 'faq' ? 'default' : 'ghost'}
                onClick={() => onNavigate?.('faq')}
                className={currentPage === 'faq' ? 'sprintbac-gradient' : 'text-green-600 hover:text-green-700'}
              >
                <HelpCircle className="mr-2 h-4 w-4" />
                FAQ
              </Button>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="sprintbac-gradient text-white font-semibold">
                        {profile?.name ? getInitials(profile.name) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{profile?.name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                      <div className="flex items-center gap-1">
                        <GraduationCap className="h-3 w-3" />
                        <span className="text-xs capitalize">{profile?.role}</span>
                        {profile?.role === 'teacher' && (
                          <span className="text-xs bg-blue-500 text-white px-1 rounded ml-1">PROF</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowProfile(true)}>
                    <User className="mr-2 h-4 w-4" />
                    Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Profile Modal - DÉSACTIVÉ POUR SÉCURITÉ */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Mon profil
            </DialogTitle>
            <DialogDescription>
              MODIFICATION BLOQUÉE POUR SÉCURITÉ
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile-name">Nom complet</Label>
              <Input
                id="profile-name"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-email">Email</Label>
              <Input
                id="profile-email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-role">Rôle</Label>
              <Select 
                value={profileForm.role} 
                onValueChange={(value: 'student' | 'teacher') => 
                  setProfileForm({ ...profileForm, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Étudiant</SelectItem>
                  <SelectItem value="teacher">Enseignant</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile-subscription">Type d'abonnement</Label>
              <Select 
                value={profileForm.subscription_type} 
                onValueChange={(value: 'visitor' | 'membre') => 
                  setProfileForm({ ...profileForm, subscription_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visitor">Visiteur</SelectItem>
                  <SelectItem value="membre">Membre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowProfile(false)}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="sprintbac-gradient hover:opacity-90"
                disabled={updating}
              >
                {updating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Mise à jour...
                  </>
                ) : (
                  'Sauvegarder'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};